import type { Period } from '@empathyco/playboard-core-domain'
import type { AppMessages } from '../models'
import { PeriodEnum, SeasonEnum } from '../enums'

export class DateUtils {
  static isDateToday(date: Date): boolean {
    const today = new Date()
    return today.toDateString() === date.toDateString()
  }

  static isCurrentYear(date: Date): boolean {
    const today = new Date()
    return today.getFullYear() === date.getFullYear()
  }

  static isEndOfMonth(date: Date): boolean {
    const oneDayAfterDate = new Date(date)
    oneDayAfterDate.setDate(date.getDate() + 1)
    return oneDayAfterDate.getDate() === 1
  }

  static isStartOfMonth(date: Date): boolean {
    return date.getDate() === 1
  }

  static isFutureDate(date: Date): boolean {
    const todayAtMidnight = new Date()
    todayAtMidnight.setHours(0, 0, 0, 0)

    const dateAtMidnight = new Date(date)
    dateAtMidnight.setHours(0, 0, 0, 0)

    return dateAtMidnight > todayAtMidnight
  }

  static areDatesEqual(date1: Date, date2: Date): boolean {
    return (
      date1 instanceof Date &&
      date2 instanceof Date &&
      date1.toDateString() === date2.toDateString()
    )
  }

  static addMonths(date: Date, months: number): Date {
    const newDate = new Date(date)
    newDate.setMonth(date.getMonth() + months)
    return newDate
  }

  static isSameMonthAndYear(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth()
  }

  static getElapsedTimeString(millis: number, messages: AppMessages['date']): string {
    if (millis === 0) {
      return messages.elapsedTime.zero
    }
    const timePeriods: string[] = []
    let hours = Math.floor(millis / 1000 / 60 / 60)
    let days = Math.floor(hours / 24)
    hours -= days * 24
    const years = Math.floor(days / 365)
    if (years > 0) {
      const yearsMessage = years > 1 ? messages.year.plural : messages.year.singular
      timePeriods.push(`${years} ${yearsMessage}`)
    }
    days -= years * 365
    const months = Math.floor(days / 30)
    if (months > 0) {
      const monthsMessage = months > 1 ? messages.month.plural : messages.month.singular
      timePeriods.push(`${months} ${monthsMessage}`)
    }
    days -= months * 30
    if (days > 0) {
      const daysMessage = days > 1 ? messages.day.plural : messages.day.singular
      timePeriods.push(`${days} ${daysMessage}`)
    }
    if (hours > 0) {
      const hoursMessage = hours > 1 ? messages.hour.plural : messages.hour.singular
      timePeriods.push(`${hours} ${hoursMessage}`)
    }
    if (!timePeriods.length) {
      return `${messages.elapsedTime.lessThan} 1 ${messages.hour.singular}`
    }
    return timePeriods.join(' ')
  }

  static getPeriodLocalRange(period: Partial<Period<Date>>): string {
    return `${period.start?.toLocaleDateString()} - ${period.end?.toLocaleDateString()}`
  }

  static getPeriodWithoutHours(period: Partial<Period<Date>>): Partial<Period<Date>> {
    if (period.start) {
      period.start.setHours(0, 0, 0)
    }
    if (period.end) {
      period.end.setHours(23, 59, 59)
    }
    return period
  }

  static getSeason(date: Date): SeasonEnum {
    const dateMonthDayFormat = (date.getMonth() + 1) * 100 + date.getDate()
    if (dateMonthDayFormat >= 320 && dateMonthDayFormat < 620) {
      return SeasonEnum.SPRING
    } else if (dateMonthDayFormat >= 620 && dateMonthDayFormat < 920) {
      return SeasonEnum.SUMMER
    } else if (dateMonthDayFormat >= 920 && dateMonthDayFormat < 1220) {
      return SeasonEnum.FALL
    }
    return SeasonEnum.WINTER
  }

  static isSamePeriod(period: PeriodEnum, date1: Date, date2: Date): boolean {
    return {
      [PeriodEnum.DAY]: () => {
        return (
          date1.getUTCDate() === date2.getUTCDate() &&
          date1.getUTCMonth() === date2.getUTCMonth() &&
          date1.getUTCFullYear() === date2.getUTCFullYear()
        )
      },
    }[period]()
  }

  static getClockTimeFromMilliseconds(milliseconds: number): string {
    const seconds = milliseconds / 1000
    const hour = Math.floor(seconds / 3600)
    const minute = Math.floor((seconds / 60) % 60)
    const second = Math.floor(seconds % 60)
    return `${hour < 10 ? `0${String(hour)}` : hour}:${
      minute < 10 ? `0${String(minute)}` : minute
    }:${second < 10 ? `0${String(second)}` : second}`
  }

  static formatDateAsMonth(date: Date, locale: string): string {
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
    }
    if (!DateUtils.isCurrentYear(date)) {
      options.year = 'numeric'
    }
    return new Intl.DateTimeFormat(locale, options).format(date)
  }

  static formatDateAsDay(date: Date, locale: string): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
    return new Intl.DateTimeFormat(locale, options).format(date)
  }

  static convertUTCToLocalDisplay(utcDate: Date): Date {
    return new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000)
  }

  static getISOStringWithoutMillisecondsAndZ(date: Date): string {
    return date.toISOString().split('.')[0]
  }
}
